import { useStyletron } from '@thanx/uikit/theme'
import thanxMark from 'assets/images/order-feedback/thanx-setup-mark.png'
import imgWordmark from 'assets/images/recovery-ai/img_wordmark.svg'
import ArrowRight from 'components/Icons/ArrowRight'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { Link } from 'react-router-dom'
// Cross-scene import of the RecoveryAI route literals, the same deviation
// scenes/Home/NeedsAttention already makes: routes.ts is the declared single
// owner of those paths, so re-typing '/recovery_ai' here is the thing it exists
// to prevent.
import { RECOVERY_AI_ROOT } from 'scenes/RecoveryAI/routes'
import { SetupCardState } from './setupCardState'

const t = buildTranslate(
  'order_ratings.settings.feedback_collection.current_setup'
)

// The Details tab of this same page — the feedback list. Mirrors the page's own
// `Tabs.DETAILS` fragment; that enum is private to OrderFeedbackReport/index.tsx
// and this ticket must not widen it, so the literal is named here instead of
// re-typed at the call site.
const ALL_FEEDBACK_PATH = '/order_feedback#details'

// Badge geometry. The shared components/Badge is deliberately NOT used: it
// takes a string `value` and renders text only, while the teal chip here draws
// the RecoveryAI wordmark as an image alongside its label. Every number below
// is measured off prototype v52's own computed styles, not eyeballed from a
// screenshot.
const CARD_PADDING_VERTICAL = '22px'
const CARD_PADDING_HORIZONTAL = '24px'
const CARD_RADIUS = '14px'
const CARD_BORDER_COLOR = '#C3C9CF'

const BADGE_FONT_SIZE = '10.5px'
const BADGE_LETTER_SPACING = '0.42px'
const BADGE_LINE_HEIGHT = '10.5px'
const BADGE_PADDING_VERTICAL = '5px'
const BADGE_PADDING_HORIZONTAL = '11px'
const BADGE_RADIUS = '20px'
const BADGE_GAP = '8px'
// The row straddles the top border: pulled up 12px, indented 21px.
const BADGE_ROW_TOP = '-12px'
const BADGE_ROW_LEFT = '20px'
// The RecoveryAI chip sits at the card's top RIGHT, opposite "Your current
// setup". The row spans the full card width so the two chips stay pinned to
// their own ends instead of sitting next to each other on the left.
const BADGE_ROW_RIGHT = '20px'
// Gap between a chip's leading mark and its label.
const BADGE_LEADING_GAP = '5px'
// The shared RecoveryAI wordmark (assets/images/recovery-ai/img_wordmark.svg),
// drawn at the smaller of the two heights the Overview's PromoBlock already
// renders it at, so both pages show one lockup at one scale.
const WORDMARK_HEIGHT = '15px'
const BADGE_BORDER_COLOR = palette.grey30
// 22px below the body plus the row's own 18px in v52.
const LINKS_GAP = '40px'
// v52's means block is 53px tall for a one-line body: 15.75 label + 13 gap + 24.
const MEANS_LABEL_GAP = '13px'

// 8px from the bottom of the 52px head row to the means label.
const MEANS_GAP = '8px'
const MEANS_LABEL_FONT_SIZE = '10.5px'
const MEANS_LABEL_LETTER_SPACING = '0.525px'
const MEANS_LABEL_LINE_HEIGHT = '15.75px'
const MEANS_LABEL_COLOR = palette.grey70
const BODY_FONT_SIZE = '16px'
const BODY_LINE_HEIGHT = '24px'
const BODY_COLOR = palette.grey90
const SECTION_GAP = '12px'
// The mark sits in a rounded tile on the same row as the title, measured off
// prototype v52: 52px square, 13px radius, 12px to the title, row centred.
const TILE_SIZE = '52px'
const TILE_RADIUS = '13px'
const TILE_GAP = '12px'
// v52's own teal behind the Thanx glyph. Not a palette token: the tile is the
// app icon's own brand colour, not a UI surface.
const THANX_TILE_COLOR = palette.spearmint50
const THANX_GLYPH_INSET_WIDTH = '78%'
const LINK_GAP = '16px'
const LINK_ARROW_GAP = '6px'

type CardLink = { key: string; label: string; muted: boolean; to: string }

type CardContent = {
  // The badge label. Every state but "not collecting" reads YOUR CURRENT SETUP.
  badge: string
  title: string
  body: string
  // The pilot state's second paragraph. Absent everywhere else.
  bodySecondary?: string
  // The teal RecoveryAI chip — the brand wordmark followed by "is responding
  // automatically" — in the three RecoveryAI states only.
  showRecoveryAiBadge: boolean
  links: CardLink[]
}

// Built per render, not held as module constants: `t()` reads the translation
// store at call time, which is not yet loaded when this module is first
// evaluated.
const viewAllFeedbackLink = (): CardLink => ({
  key: 'view_all_feedback',
  label: t('links.view_all_feedback'),
  muted: false,
  to: ALL_FEEDBACK_PATH,
})

// Always the SECOND link, so it always takes the muted treatment.
const manageRecoveryAiLink = (): CardLink => ({
  key: 'manage_recovery_ai',
  label: t('links.manage_recovery_ai'),
  muted: true,
  to: RECOVERY_AI_ROOT,
})

// The pilot body carries TWO independent plural axes in one sentence — the
// active count ("location resolves" / "locations resolve") and the remainder
// ("still sends" / "still send") — and react-redux-i18n pluralizes on ONE
// `%{count}` per key, so it cannot serve both. The form is therefore chosen
// here and the locale holds a four-key matrix (see orderRatings.ts).
//
// NOTHING named `count` may be passed to `t` for these keys: the library would
// append its own `_plural` to the key picked here and render the raw key.
//
// Grammar only — the four strings differ in two verbs and nothing else.
const pilotBodyKey = (active: number, remaining: number): string => {
  if (active === 1) {
    return remaining === 1
      ? 'rai_pilot.body'
      : 'rai_pilot.body_remaining_plural'
  }

  return remaining === 1
    ? 'rai_pilot.body_active_plural'
    : 'rai_pilot.body_active_plural_remaining_plural'
}

// A COPY MAP over an already-decided union, not a second resolver: every branch
// below is keyed on `state.kind` and decides nothing. The decision lives in
// setupCardState.ts (AI-1257 replaces that one module). `not_collecting` is
// excluded here: the card renders nothing for that state (see the early return
// below), so it has no copy to map.
const contentFor = (
  state: Exclude<SetupCardState, { kind: 'not_collecting' }>
): CardContent => {
  switch (state.kind) {
    case 'thanx':
      return {
        badge: t('badge'),
        title: t('thanx.title'),
        body: t('thanx.body'),
        showRecoveryAiBadge: false,
        links: [viewAllFeedbackLink()],
      }
    case 'rai_pilot': {
      const { activeLocations, totalLocations } = state
      // DERIVED, never carried in from the union and never hardcoded.
      const remainingLocations = totalLocations - activeLocations
      return {
        badge: t('badge'),
        title: t('rai_pilot.title', {
          active: activeLocations,
          total: totalLocations,
        }),
        body: t(pilotBodyKey(activeLocations, remainingLocations), {
          active: activeLocations,
          remaining: remainingLocations,
        }),
        bodySecondary: t('rai_pilot.body_secondary', {
          remaining: remainingLocations,
        }),
        showRecoveryAiBadge: true,
        links: [viewAllFeedbackLink(), manageRecoveryAiLink()],
      }
    }
    case 'rai_all_locations':
      return {
        badge: t('badge'),
        title: t('rai_all_locations.title'),
        body: t('rai_all_locations.body'),
        showRecoveryAiBadge: true,
        links: [viewAllFeedbackLink(), manageRecoveryAiLink()],
      }
  }
}

type BadgeProps = {
  backgroundColor: string
  color: string
  // Drawn inside the pill, before the label. The RecoveryAI chip puts the brand
  // wordmark here rather than spelling "RecoveryAI" out in text.
  leading?: React.ReactNode
  children: string
}

const SetupBadge = ({
  backgroundColor,
  color,
  leading,
  children,
}: BadgeProps) => {
  const [css] = useStyletron()

  return (
    <span
      className={css({
        backgroundColor,
        color,
        display: 'inline-flex',
        alignItems: 'center',
        gap: BADGE_LEADING_GAP,
        fontSize: BADGE_FONT_SIZE,
        fontWeight: 800,
        letterSpacing: BADGE_LETTER_SPACING,
        lineHeight: BADGE_LINE_HEIGHT,
        textTransform: 'uppercase',
        paddingTop: BADGE_PADDING_VERTICAL,
        paddingBottom: BADGE_PADDING_VERTICAL,
        paddingLeft: BADGE_PADDING_HORIZONTAL,
        paddingRight: BADGE_PADDING_HORIZONTAL,
        borderTopLeftRadius: BADGE_RADIUS,
        borderTopRightRadius: BADGE_RADIUS,
        borderBottomLeftRadius: BADGE_RADIUS,
        borderBottomRightRadius: BADGE_RADIUS,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: BADGE_BORDER_COLOR,
      })}
    >
      {leading}
      {children}
    </span>
  )
}

type LinkRowProps = {
  links: CardLink[]
}

const SetupLinks = ({ links }: LinkRowProps) => {
  const [css] = useStyletron()

  const labelClass = (muted: boolean) =>
    css({
      color: muted ? palette.grey70 : palette.belizeHole,
      fontSize: '14px',
      fontWeight: 700,
      lineHeight: '21px',
      display: 'inline-flex',
      alignItems: 'center',
      textDecoration: 'none',
    })

  // Decorative: each label already says where the link goes, so the glyph stays
  // out of the accessible name. From the icon library rather than a literal
  // arrow — raw Unicode in JSX is not allowed.
  const arrow = (
    <span
      aria-hidden="true"
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: LINK_ARROW_GAP,
      })}
    >
      <ArrowRight />
    </span>
  )

  return (
    <div
      className={css({
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
        gap: LINK_GAP,
        marginTop: LINKS_GAP,
      })}
    >
      {links.map(link => (
        <Link key={link.key} to={link.to} className={labelClass(link.muted)}>
          {link.label}
          {arrow}
        </Link>
      ))}
    </div>
  )
}

type Props = {
  state: SetupCardState
}

/**
 * FR-22's "Your current setup" card, below the written-feedback toggle in the
 * Feedback collection sub-tab. Renders nothing at all while written feedback
 * is off — not hidden, not empty, simply not in the tree.
 *
 * Presentational and fully driven by the `SetupCardState` union: no slice, no
 * query, no permission check. The card is READ-ONLY, so it carries no
 * `useSettingsEditPermission` gate — a viewer without `manage MerchantSettings`
 * sees exactly what an editor sees (FR-60 gates controls, not viewing).
 */
const CurrentSetupCard = ({ state }: Props) => {
  const [css] = useStyletron()

  if (state.kind === 'not_collecting') return null

  const { badge, title, body, bodySecondary, showRecoveryAiBadge, links } =
    contentFor(state)

  return (
    // A plain div, not `Section`: Section's own padding, radius and border win
    // over an override class, and every one of those three differs from v52.
    <div
      className={`mb-m ${css({
        position: 'relative',
        overflow: 'visible',
        paddingTop: CARD_PADDING_VERTICAL,
        paddingBottom: CARD_PADDING_VERTICAL,
        paddingLeft: CARD_PADDING_HORIZONTAL,
        paddingRight: CARD_PADDING_HORIZONTAL,
        borderRadius: CARD_RADIUS,
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: CARD_BORDER_COLOR,
        backgroundColor: palette.white,
      })}`}
    >
      {/* The badge row straddles the card's top border, fieldset-legend style,
          rather than sitting inside it. Taken out of flow and pulled up by half
          its own height; each chip carries the page background so the border
          reads as interrupted rather than crossed out. */}
      <div
        className={css({
          position: 'absolute',
          top: BADGE_ROW_TOP,
          left: BADGE_ROW_LEFT,
          right: BADGE_ROW_RIGHT,
          display: 'flex',
          flexWrap: 'wrap',
          // "Your current setup" stays left, the RecoveryAI chip goes right.
          // With one chip the lone chip stays left, which is what the states
          // without RecoveryAI want.
          justifyContent: 'space-between',
          gap: BADGE_GAP,
        })}
      >
        <SetupBadge backgroundColor={palette.grey10} color={palette.grey90}>
          {badge}
        </SetupBadge>
        {showRecoveryAiBadge && (
          // Design's teal chip, with the label in `grey90` rather than a teal.
          // The teal background carries the RecoveryAI cue; the text does not
          // have to. `spearmint60` is the darkest teal the palette has and it
          // reads 3.03:1 on `spearmint10`, under WCAG AA's 4.5:1 for text this
          // size (`spearmint50`, the RoleBadge precedent, is 2.05:1). `grey90`
          // on the same background is 9.44:1, so the chip keeps its colour and
          // the label is legible.
          <SetupBadge
            backgroundColor={palette.spearmint10}
            color={palette.grey90}
            leading={
              <img
                src={imgWordmark}
                alt={t('recovery_ai_badge_wordmark_alt')}
                className={css({
                  height: WORDMARK_HEIGHT,
                  width: 'auto',
                  display: 'block',
                })}
              />
            }
          >
            {t('recovery_ai_badge')}
          </SetupBadge>
        )}
      </div>

      {/* Mark and title share one centred row, per v52. The mark is decorative:
          the title names the tool in words. */}
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
        })}
      >
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: TILE_SIZE,
            height: TILE_SIZE,
            borderRadius: TILE_RADIUS,
            marginRight: TILE_GAP,
            flexShrink: 0,
            overflow: 'hidden',
            backgroundColor: THANX_TILE_COLOR,
          })}
        >
          <img
            src={thanxMark}
            alt=""
            className={css({
              display: 'block',
              width: THANX_GLYPH_INSET_WIDTH,
              height: 'auto',
              objectFit: 'contain',
            })}
          />
        </div>
        <h4
          className={css({
            margin: 0,
            fontSize: BODY_FONT_SIZE,
            lineHeight: BODY_LINE_HEIGHT,
            fontWeight: 700,
            color: BODY_COLOR,
          })}
        >
          {title}
        </h4>
      </div>

      <div
        className={css({
          color: MEANS_LABEL_COLOR,
          fontSize: MEANS_LABEL_FONT_SIZE,
          fontWeight: 800,
          letterSpacing: MEANS_LABEL_LETTER_SPACING,
          lineHeight: MEANS_LABEL_LINE_HEIGHT,
          textTransform: 'uppercase',
          marginTop: MEANS_GAP,
        })}
      >
        {t('means_label')}
      </div>

      <div
        className={css({
          fontSize: BODY_FONT_SIZE,
          lineHeight: BODY_LINE_HEIGHT,
          color: BODY_COLOR,
          marginTop: MEANS_LABEL_GAP,
        })}
      >
        {body}
      </div>

      {bodySecondary && (
        <div
          className={css({
            fontSize: BODY_FONT_SIZE,
            lineHeight: BODY_LINE_HEIGHT,
            color: BODY_COLOR,
            marginTop: SECTION_GAP,
          })}
        >
          {bodySecondary}
        </div>
      )}

      <SetupLinks links={links} />
    </div>
  )
}

export default CurrentSetupCard
